import {
  Connection,
  PublicKey,
  RpcResponseAndContext,
  SignatureResult,
  Transaction,
  VersionedTransaction,
} from '@solana/web3.js'
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MintLayout,
  RawMint,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import { awaitTransactionSignatureConfirmation } from '@blockworks-foundation/mangolana/lib/transactions'
import { notify } from 'utils/notifications'
import { BN } from '@coral-xyz/anchor'
import { LITE_RPC_BACKUP_URL, LITE_RPC_URL } from '../constants'

const backupRpcConnections = [
  new Connection(LITE_RPC_URL),
  new Connection(LITE_RPC_BACKUP_URL),
] as const

export async function tryGetMint(connection: Connection, publicKey: PublicKey) {
  try {
    const result = await connection.getAccountInfo(publicKey)
    const data = Buffer.from(result!.data)
    const account = parseMintAccountData(data)
    return {
      publicKey,
      account,
    }
  } catch (ex) {
    console.error(
      `Can't fetch mint ${publicKey?.toBase58()} @ ${connection.rpcEndpoint}`,
      ex,
    )
    return undefined
  }
}

export function parseMintAccountData(data: Buffer): RawMint {
  const mintInfo = MintLayout.decode(data)
  return mintInfo
}

export const fmtTokenAmount = (c: BN, decimals?: number) =>
  c?.div(new BN(10).pow(new BN(decimals ?? 0))).toNumber() || 0

export const tryGetPubKey = (pubkey: string | null | undefined) => {
  if (!pubkey) {
    return null
  }

  try {
    return new PublicKey(pubkey)
  } catch (e) {
    return null
  }
}

const urlRegex =
  // eslint-disable-next-line
  /(https:\/\/)(gist\.github.com\/)([\w\/]{1,39}\/)(\w{1,32})/

export async function fetchGistFile(gistUrl: string) {
  const controller = new AbortController()
  const pieces = gistUrl.match(urlRegex)
  if (pieces) {
    const justIdWithoutUser = pieces[4]
    if (justIdWithoutUser) {
      const apiUrl = `https://api.github.com/gists/${encodeURIComponent(
        justIdWithoutUser,
      )}`
      const apiResponse = await fetch(apiUrl, {
        signal: controller.signal,
      })
      const jsonContent = (await apiResponse.json()) as {
        files: Record<string, { raw_url: string }>
      }
      if (apiResponse.ok) {
        const nextUrlFileName = Object.keys(jsonContent.files)[0]
        const nextUrl = jsonContent.files[nextUrlFileName].raw_url
        if (nextUrl.startsWith('https://gist.githubusercontent.com/')) {
          const fileResponse = await fetch(nextUrl, {
            signal: controller.signal,
          })
          const body = await fileResponse.json()
          //console.log('fetchGistFile file', gistUrl, fileResponse)
          return body
        }
        return undefined
      } else {
        console.warn('could not fetchGistFile', {
          gistUrl,
          apiResponse: jsonContent,
        })
      }
    }
  }

  return undefined
}

export async function resolveProposalDescription(descriptionLink: string) {
  try {
    const url = new URL(descriptionLink)
    const desc = (await fetchGistFile(url.toString())) ?? descriptionLink
    return desc
  } catch {
    return descriptionLink
  }
}

export const compareObjectsAndGetDifferentKeys = <T extends object>(
  object1: T,
  object2: T,
): (keyof T)[] => {
  const diffKeys: string[] = []
  Object.keys(object1).forEach((key) => {
    if (
      object1[key as keyof typeof object1] !==
      object2[key as keyof typeof object2]
    ) {
      diffKeys.push(key)
    }
  })

  return diffKeys as (keyof T)[]
}

export const sendTxAndConfirm = async (
  connection: Connection,
  tx: Transaction | VersionedTransaction,
  latestBlockhash: {
    lastValidBlockHeight: number
    blockhash: string
  },
  showNotification:
    | {
        transactionSent: string
        waitingForConfirmation: string
      }
    | false,
) => {
  let signature = ''
  const abortController = new AbortController()
  try {
    const allConnections = [connection, ...backupRpcConnections]
    const rawTransaction = tx.serialize()
    signature = await Promise.any(
      allConnections.map((c) => {
        return c.sendRawTransaction(rawTransaction, {
          skipPreflight: false,
        })
      }),
    )
    if (showNotification) {
      notify({
        title: showNotification.transactionSent,
        description: showNotification.waitingForConfirmation,
        type: 'confirm',
        txid: signature,
      })
    }
    await Promise.any(
      allConnections.map((c) =>
        awaitTransactionSignatureConfirmation({
          txid: signature,
          confirmLevel: 'confirmed',
          connection: c,
          timeoutStrategy: {
            block: latestBlockhash,
          },
          abortSignal: abortController.signal,
        }),
      ),
    )
    abortController.abort()
    return signature
  } catch (e) {
    abortController.abort()

    let error: unknown = e
    if (error instanceof AggregateError) {
      error = error.errors[0]
    }
    throw new TransactionError({
      txid: signature,
      message: stringifyError(error),
      contextSlot: isErrorWithSignatureResult(error)
        ? error.context.slot
        : undefined,
    })
  }
}

export function stringifyError(e: unknown) {
  if (isErrorWithSignatureResult(e)) {
    e = e?.value?.err
  }
  return String(
    (e instanceof Error ? e.message : tryStringify(e)) ?? e ?? 'Unknown error',
  )
}

export function isErrorWithSignatureResult(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: unknown,
): err is RpcResponseAndContext<SignatureResult> {
  return (
    err != null &&
    typeof err === 'object' &&
    'context' in err &&
    err.context != null &&
    'value' in err &&
    err.value != null
  )
}

export class TransactionError extends Error {
  readonly txid: string
  readonly contextSlot?: number

  constructor({
    txid,
    message,
    contextSlot,
  }: {
    txid: string
    message: string
    contextSlot?: number
  }) {
    super()
    this.message = message
    this.txid = txid
    this.contextSlot = contextSlot
  }
}

export const tryStringify = (val: unknown): string | null => {
  try {
    return JSON.stringify(val)
  } catch {
    return null
  }
}

export function getAssociatedTokenAddress(
  mint: PublicKey,
  owner: PublicKey,
  allowOwnerOffCurve = true,
  programId = TOKEN_PROGRAM_ID,
  associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID,
): PublicKey {
  if (!allowOwnerOffCurve && !PublicKey.isOnCurve(owner.toBuffer()))
    throw new Error('TokenOwnerOffCurve!')

  const [address] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), programId.toBuffer(), mint.toBuffer()],
    associatedTokenProgramId,
  )

  return address
}
